/**
 * SFrame107 – Deceased Information (Step 3 of 3)
 * Figma Frame 7 (node 1018:13624)
 *
 * "We are sorry for your loss. Simply filling out this page is a big step,
 *  it is okay to take as much time as you need."
 *
 * Collects: Name of the deceased, Date of passing.
 */

import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../../utils/api';

interface LocationState {
  situationType?: string;
  role?: string;
}

export default function SFrame107() {
  const navigate = useNavigate();
  const location = useLocation();
  const { situationType, role } = (location.state as LocationState) || {};

  const [deceasedFirstName, setDeceasedFirstName] = useState('');
  const [deceasedLastName, setDeceasedLastName] = useState('');
  const [dateOfPassing, setDateOfPassing] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isFormValid = deceasedFirstName.trim() && deceasedLastName.trim() && dateOfPassing;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    setIsSubmitting(true);
    setError('');

    try {
      const response = await apiService.request('/api/settlement/init', {
        method: 'POST',
        body: JSON.stringify({
          deceasedFirstName: deceasedFirstName.trim(),
          deceasedLastName: deceasedLastName.trim(),
          dateOfPassing,
          situationType,
          role,
        }),
      });

      if (response.success) {
        navigate('/sframe-109', {
          state: {
            deceasedName: `${deceasedFirstName.trim()} ${deceasedLastName.trim()}`,
            estateId: response.estateId,
          },
        });
      } else {
        setError(response.message || 'Failed to save information. Please try again.');
      }
    } catch (err) {
      console.error('[SFrame107] Init settlement error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top header bar */}
      <div className="bg-dark-gradient py-3 px-6">
        <p className="text-center text-white-alt font-semibold text-base tracking-wide">
          Estate Settlement
        </p>
      </div>

      {/* Background: cream top, light blue bottom */}
      <div className="flex-1 flex items-center justify-center px-4 py-12 bg-gradient-to-b from-white to-blue-100">
        <div className="w-full max-w-[520px] bg-white-alt rounded-xl shadow-card p-8 sm:p-10">
          <h1 className="font-display text-xl sm:text-2xl text-blue-900 mb-8 leading-snug">
            We are sorry for your loss. Simply filling out this page is a big step, it is okay to take as much time as you need.
          </h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name of the deceased */}
            <div>
              <label htmlFor="deceasedName" className="block text-sm font-semibold text-black mb-2">
                Name of the deceased
              </label>
              <div className="grid grid-cols-2 gap-3">
                <input
                  id="deceasedName"
                  type="text"
                  value={deceasedFirstName}
                  onChange={(e) => setDeceasedFirstName(e.target.value)}
                  className="w-full rounded-md border border-grey-300 px-4 py-3 text-sm text-black placeholder:text-grey-600 focus:border-blue-600 focus:ring-1 focus:ring-blue-600 outline-none transition"
                  placeholder="First"
                  required
                />
                <input
                  type="text"
                  value={deceasedLastName}
                  onChange={(e) => setDeceasedLastName(e.target.value)}
                  className="w-full rounded-md border border-grey-300 px-4 py-3 text-sm text-black placeholder:text-grey-600 focus:border-blue-600 focus:ring-1 focus:ring-blue-600 outline-none transition"
                  placeholder="Last"
                  required
                />
              </div>
            </div>

            {/* Date of passing */}
            <div>
              <label htmlFor="dateOfPassing" className="block text-sm font-semibold text-black mb-2">
                Date of passing
              </label>
              <input
                id="dateOfPassing"
                type="text"
                value={dateOfPassing}
                onChange={(e) => setDateOfPassing(e.target.value)}
                onFocus={(e) => { e.target.type = 'date'; }}
                onBlur={(e) => { if (!e.target.value) e.target.type = 'text'; }}
                max={new Date().toISOString().split('T')[0]}
                className="w-full rounded-md border border-grey-300 px-4 py-3 text-sm text-black placeholder:text-grey-600 focus:border-blue-600 focus:ring-1 focus:ring-blue-600 outline-none transition"
                placeholder="MM/DD/YYYY"
                required
              />
            </div>

            {/* Error */}
            {error && (
              <div className="bg-orange-100 border border-orange-300 rounded-md p-3 text-orange-900 text-sm">
                {error}
              </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={() => navigate('/sframe-105', { state: { situationType } })}
                className="text-black hover:text-blue-900 text-sm font-medium transition-colors flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
              <button
                type="submit"
                disabled={!isFormValid || isSubmitting}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-md font-medium text-sm transition-all ${
                  isFormValid && !isSubmitting
                    ? 'bg-blue-300 text-white-alt hover:bg-blue-600'
                    : 'bg-grey-300 text-grey-600 cursor-not-allowed'
                }`}
              >
                {isSubmitting ? 'Saving...' : 'Finish'}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
